/**
 * PrintableEmployeeBadge.jsx · v2.7.63+E48
 * ─────────────────────────────────────────────────────────────────────
 * Print-ready 3.375" × 2.125" (CR80 credit-card-standard) employee ID
 * badge. Renders inside a dialog + fires window.print() with a
 * dedicated @media print stylesheet so the browser's print dialog shows
 * ONLY the badge — no admin dashboard chrome.
 *
 * Elements on the card:
 *   • Buller Taxi logo/wordmark (top strip · amber accent bar)
 *   • Operator photo (from driver.photo_url · falls back to a generated
 *     amber circle with initials)
 *   • Full name (large, bold)
 *   • Role pill (Dispatcher / Supervisor / General Partner / etc.)
 *   • Employee ID as a Code-128 barcode (jsbarcode)
 *   • QR code deep-linking to `/admin/drivers/{id}` for scan-and-open
 *     workflows
 *   • "AUTHORIZED · Buller Taxi Services" footer stripe (emerald)
 *
 * Usage:
 *   <PrintableEmployeeBadge driver={driverDoc}/>
 *
 * A parent dialog handles open/close state.
 */
import { useEffect, useMemo, useRef } from "react";
import JsBarcode from "jsbarcode";
import { QRCodeSVG } from "qrcode.react";
import { Printer, X, IdCard } from "lucide-react";
import { Button } from "@/components/ui/button";

function initialsOf(driver) {
  const first = driver?.first_name || (driver?.name?.split(" ")[0] || "");
  const last  = driver?.last_name  || (driver?.name?.split(" ").slice(-1)[0] || "");
  return `${(first[0] || "").toUpperCase()}${(last[0] || "").toUpperCase()}` || "??";
}

function fullNameOf(driver) {
  const first = driver?.first_name || "";
  const last  = driver?.last_name  || "";
  const combined = [first, last].filter(Boolean).join(" ").trim();
  return combined || driver?.name || "Operator";
}

function roleLabelOf(driver) {
  const map = {
    driver:            "DRIVER",
    dispatcher:        "DISPATCHER",
    team_leader:       "TEAM LEADER",
    field_supervisor:  "FIELD SUPERVISOR",
    supervisor:        "SUPERVISOR",
    operating_partner: "SUPERVISOR",
    developer:         "DEVELOPER",
    admin:             "ADMIN",
  };
  return map[driver?.role] || (driver?.role || "OPERATOR").toUpperCase();
}


export default function PrintableEmployeeBadge({ driver, onClose }) {
  const barcodeRef = useRef(null);
  const empId = driver?.employee_id || "";
  const profileUrl = `${window.location.origin}/admin/drivers/${driver?.id || ""}`;
  const initials = useMemo(() => initialsOf(driver), [driver]);
  const fullName = useMemo(() => fullNameOf(driver), [driver]);
  const roleLabel = useMemo(() => roleLabelOf(driver), [driver]);

  useEffect(() => {
    if (!barcodeRef.current || !empId) return;
    try {
      JsBarcode(barcodeRef.current, empId, {
        format: "CODE128",
        displayValue: false,
        height: 40,
        margin: 0,
        background: "#ffffff",
        lineColor: "#000000",
        width: 1.6,
      });
    } catch (e) {
      console.warn("[EmployeeBadge] barcode render failed:", e);
    }
  }, [empId]);

  const print = () => {
    // Give the print stylesheet a chance to apply by adding a body flag,
    // then invoke the browser print dialog. The @media print block
    // hides everything except .printable-badge-card so the page prints
    // just the badge at exact CR80 dimensions.
    document.body.dataset.printingBadge = "true";
    // v2.7.63+E48.1 · Safari doesn't reliably fire `afterprint`, so we
    // use a defensive setTimeout fallback (900ms — long enough for the
    // print dialog to appear, short enough to auto-recover for the
    // next print action).
    const cleanup = () => {
      delete document.body.dataset.printingBadge;
      window.removeEventListener("afterprint", cleanup);
      clearTimeout(safetyNet);
    };
    let safetyNet = null;
    setTimeout(() => {
      window.print();
      window.addEventListener("afterprint", cleanup);
      safetyNet = setTimeout(cleanup, 15000);
    }, 50);
  };

  return (
    <>
      {/* Print-only stylesheet — hides everything except the badge card
          when window.print() fires, and sizes the page to CR80. */}
      <style>{`
        @media print {
          body[data-printing-badge="true"] * {
            visibility: hidden;
          }
          body[data-printing-badge="true"] .printable-badge-card,
          body[data-printing-badge="true"] .printable-badge-card * {
            visibility: visible;
          }
          body[data-printing-badge="true"] .printable-badge-card {
            position: fixed;
            top: 0;
            left: 0;
            margin: 0;
            box-shadow: none !important;
            transform: none !important;
          }
          @page {
            size: 3.375in 2.125in;
            margin: 0;
          }
        }
      `}</style>

      <div
        className="fixed inset-0 z-[10000] bg-zinc-950/85 backdrop-blur flex items-center justify-center p-4 no-print"
        data-testid="printable-badge-modal"
        onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      >
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-md w-full">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <IdCard className="h-4 w-4 text-amber-300"/>
              <div>
                <div className="font-bold text-sm text-zinc-100">Print Employee ID Badge</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  CR80 · 3.375&quot; × 2.125&quot;
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-zinc-500 hover:text-zinc-200"
              data-testid="printable-badge-close"
            >
              <X className="h-4 w-4"/>
            </button>
          </div>

          {/* Live preview at 2× scale so the operator can inspect
              alignment before hitting Print. The @media print rule
              above resets to real dimensions. */}
          <div className="flex items-center justify-center bg-zinc-950 p-4 rounded border border-zinc-800 mb-4">
            <div
              className="printable-badge-card"
              data-testid="printable-badge-card"
              style={{
                width: "3.375in",
                height: "2.125in",
                background: "#ffffff",
                color: "#0a0a0a",
                borderRadius: "0.125in",
                boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                position: "relative",
                overflow: "hidden",
                fontFamily: "system-ui, sans-serif",
                transform: "scale(2)",
                transformOrigin: "center center",
                margin: "1.75in 0",
              }}
            >
              {/* Top amber accent strip */}
              <div style={{
                height: "0.3in", background: "linear-gradient(90deg, #f59e0b, #ea580c)",
                display: "flex", alignItems: "center", padding: "0 0.15in",
                color: "#0a0a0a", fontSize: "0.09in", fontWeight: 900,
                letterSpacing: "0.03in", textTransform: "uppercase",
              }}>
                Buller Taxi Services · Operator
              </div>

              {/* Main body */}
              <div style={{
                display: "grid", gridTemplateColumns: "0.85in 1fr",
                padding: "0.1in 0.15in 0 0.15in", gap: "0.1in",
              }}>
                {/* Photo or initials avatar */}
                <div style={{
                  width: "0.85in", height: "0.85in",
                  borderRadius: "0.1in",
                  background: driver?.photo_url ? `url(${driver.photo_url}) center/cover` : "linear-gradient(135deg, #f59e0b, #ea580c)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#0a0a0a", fontWeight: 900, fontSize: "0.28in",
                  border: "0.02in solid #0a0a0a",
                }}>
                  {!driver?.photo_url && initials}
                </div>

                {/* Text block */}
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: "0.16in", fontWeight: 900,
                    lineHeight: 1.1, textTransform: "uppercase",
                    overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {fullName}
                  </div>
                  <div style={{
                    fontSize: "0.08in", fontWeight: 700,
                    color: "#525252", marginTop: "0.04in",
                    textTransform: "uppercase", letterSpacing: "0.02in",
                  }}>
                    {roleLabel}
                  </div>
                  <div style={{
                    marginTop: "0.07in",
                    fontSize: "0.075in", fontWeight: 700, color: "#0a0a0a",
                    letterSpacing: "0.015in",
                  }}>
                    ID · <span style={{ fontFamily: "monospace" }}>{empId || "—"}</span>
                  </div>
                </div>
              </div>

              {/* Barcode + QR */}
              <div style={{
                position: "absolute", bottom: "0.24in", left: "0.15in", right: "0.15in",
                display: "grid", gridTemplateColumns: "1fr 0.65in", gap: "0.1in",
                alignItems: "flex-end",
              }}>
                <div>
                  {empId ? (
                    <svg
                      ref={barcodeRef}
                      style={{ width: "100%", height: "0.55in" }}
                      data-testid="printable-badge-barcode"
                    />
                  ) : (
                    <div style={{
                      fontSize: "0.07in", color: "#a3a3a3", fontStyle: "italic",
                      textAlign: "center", padding: "0.05in", border: "0.01in dashed #d4d4d4",
                    }}>
                      No Employee ID set
                    </div>
                  )}
                </div>
                <div style={{
                  width: "0.65in", height: "0.65in", padding: "0.03in",
                  background: "#ffffff", border: "0.01in solid #e5e5e5",
                  borderRadius: "0.04in",
                }}>
                  <QRCodeSVG
                    value={profileUrl}
                    size={54}
                    level="M"
                    includeMargin={false}
                    style={{ width: "100%", height: "100%" }}
                    data-testid="printable-badge-qr"
                  />
                </div>
              </div>

              {/* Bottom emerald stripe */}
              <div style={{
                position: "absolute", bottom: 0, left: 0, right: 0,
                height: "0.16in", background: "#059669",
                display: "flex", alignItems: "center", padding: "0 0.15in",
                color: "#ffffff", fontSize: "0.075in", fontWeight: 800,
                letterSpacing: "0.025in", textTransform: "uppercase",
                justifyContent: "space-between",
              }}>
                <span>Authorized</span>
                <span style={{ fontFamily: "monospace", opacity: 0.85 }}>
                  bullertaxi.com
                </span>
              </div>
            </div>
          </div>

          {!empId && (
            <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-400/40 rounded p-2 mb-3" data-testid="printable-badge-empid-warning">
              ⚠ No Employee ID set on this operator. Set one first — the badge will still print but the barcode column shows a placeholder.
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="bg-zinc-950 border-zinc-700 text-zinc-300"
              data-testid="printable-badge-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={print}
              className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-extrabold"
              data-testid="printable-badge-print"
            >
              <Printer className="h-4 w-4 mr-1.5"/>
              Print Badge
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
